import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { DenounceAdminService } from '../../../services/denounce-admin.service';
import { DenounceAccessService } from '../../../services/denounce-access.service';
import { AttachmentRef } from '../shared/attachment-viewer/attachment-viewer.component';
import { AdminAttachmentDialogComponent } from '../shared/attachment-dialog/attachment-dialog.component';
import {
  CloseCaseDialogComponent,
  CloseCaseResult,
} from '../close-case-dialog/close-case-dialog.component';
import {
  Denounce,
  DenounceOperator,
  DenounceStatus,
  DENOUNCE_STATUS_LABELS,
  DENOUNCE_STATUS_COLORS,
} from '../../../interfaces/denounce.interface';

export interface TimelineItem {
  type: 'message' | 'status';
  at: string;
  // message fields
  messageAuthorType?: 'ADMIN' | 'REPORTER';
  messageAuthorName?: string;
  messageContent?: string;
  messageAttachments?: Array<{ id: string; fileName: string; fileType: string }>;
  // status fields
  oldStatus?: string;
  newStatus?: string;
  changedByName?: string;
  notes?: string;
}

@Component({
  selector: 'app-denounce-detail',
  templateUrl: './denounce-detail.component.html',
  styleUrls: ['./denounce-detail.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DenounceDetailComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  loading = false;
  denounce: Denounce | null = null;
  operators: DenounceOperator[] = [];

  newMessageContent = '';
  messageFiles: { storageKey: string; fileName: string; fileSize: number; fileType: string }[] = [];

  statusLabels: Record<string, string> = DENOUNCE_STATUS_LABELS;
  statusColors: Record<string, string> = DENOUNCE_STATUS_COLORS;

  validTransitions: Record<string, string[]> = {
    RECIBIDA: ['EN_INVESTIGACION'],
    EN_INVESTIGACION: ['CONCLUIDA', 'DESESTIMADA'],
    CONCLUIDA: [],
    DESESTIMADA: [],
  };

  timeline: TimelineItem[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private denounceService: DenounceAdminService,
    private accessService: DenounceAccessService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.denounceService.loading$.subscribe((l) => {
        this.loading = l;
        this.cdr.markForCheck();
      }),
    );

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadDetail(id);
      this.loadOperators();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadDetail(id: string): void {
    this.denounceService.getDetail(id).subscribe({
      next: (d) => {
        this.denounce = d;
        this.timeline = this.buildTimeline(d);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.snackBar.open(err.message || 'Error al cargar detalle', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  private buildTimeline(d: Denounce): TimelineItem[] {
    const items: TimelineItem[] = [];

    for (const m of d.messages || []) {
      items.push({
        type: 'message',
        at: m.createdAt,
        messageAuthorType: m.authorType,
        messageAuthorName:
          m.authorType === 'ADMIN'
            ? m.author?.operatorName || 'Operador'
            : 'Denunciante',
        messageContent: m.content,
        messageAttachments: (m.attachments || []).map((a) => ({
          id: a.id,
          fileName: a.fileName,
          fileType: a.fileType,
        })),
      });
    }

    for (const h of d.history || []) {
      items.push({
        type: 'status',
        at: h.createdAt,
        oldStatus: h.oldStatus,
        newStatus: h.newStatus,
        changedByName: h.changedByOperator?.operatorName || 'Sistema',
        notes: h.notes,
      });
    }

    items.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    return items;
  }

  trackTimeline(_: number, it: TimelineItem): string {
    return `${it.type}-${it.at}`;
  }

  loadOperators(): void {
    this.accessService.listOperators().subscribe({
      next: (ops) => {
        this.operators = ops.filter((o) => o.isActive);
        this.cdr.markForCheck();
      },
    });
  }

  getAvailableTransitions(): string[] {
    if (!this.denounce) return [];
    return this.validTransitions[this.denounce.status] || [];
  }

  async changeStatus(newStatus: string): Promise<void> {
    if (!this.denounce) return;

    const isClosing = newStatus === 'CONCLUIDA' || newStatus === 'DESESTIMADA';

    if (isClosing) {
      // Dialog con mensaje final + adjuntos + notas internas
      const ref = this.dialog.open(CloseCaseDialogComponent, {
        data: {
          targetStatus: newStatus,
          statusLabel: this.statusLabels[newStatus as DenounceStatus],
        },
        autoFocus: false,
      });
      const res = await ref.afterClosed().toPromise();
      if (!res) return;
      const closeResult = res as CloseCaseResult;
      this.denounceService
        .updateStatus(
          this.denounce.id,
          newStatus,
          closeResult.notes,
          closeResult.finalMessage,
          closeResult.attachmentKeys,
        )
        .subscribe({
          next: () => {
            this.snackBar.open('Caso cerrado', 'Cerrar', { duration: 3000 });
            this.loadDetail(this.denounce!.id);
          },
          error: (err) => {
            this.snackBar.open(err.message || 'Error al cerrar el caso', 'Cerrar', {
              duration: 3000,
            });
          },
        });
      return;
    }

    // Transiciones intermedias: solo notas opcionales
    const result = await Swal.fire({
      title: 'Cambiar estado',
      text: `¿Cambiar a "${this.statusLabels[newStatus as DenounceStatus]}"?`,
      input: 'textarea',
      inputLabel: 'Notas (opcional)',
      inputPlaceholder: 'Motivo del cambio...',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1565C0',
    });

    if (!result.isConfirmed) return;

    this.denounceService
      .updateStatus(this.denounce.id, newStatus, result.value || undefined)
      .subscribe({
        next: () => {
          this.snackBar.open('Estado actualizado', 'Cerrar', { duration: 3000 });
          this.loadDetail(this.denounce!.id);
        },
        error: (err) => {
          this.snackBar.open(err.message || 'Error al cambiar estado', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  get isCaseClosed(): boolean {
    return (
      this.denounce?.status === 'CONCLUIDA' ||
      this.denounce?.status === 'DESESTIMADA'
    );
  }

  async assignOperator(operatorId: string): Promise<void> {
    if (!this.denounce) return;

    this.denounceService.assign(this.denounce.id, operatorId).subscribe({
      next: () => {
        this.snackBar.open('Responsable asignado', 'Cerrar', { duration: 3000 });
        this.loadDetail(this.denounce!.id);
      },
      error: (err) => {
        this.snackBar.open(err.message || 'Error al asignar', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }

  sendMessage(): void {
    if (!this.denounce || !this.newMessageContent.trim()) return;

    const attachmentKeys = this.messageFiles.map((f) => f.storageKey);
    this.denounceService
      .sendMessage(this.denounce.id, this.newMessageContent.trim(), attachmentKeys.length ? attachmentKeys : undefined)
      .subscribe({
        next: () => {
          this.newMessageContent = '';
          this.messageFiles = [];
          this.snackBar.open('Mensaje enviado', 'Cerrar', { duration: 3000 });
          this.loadDetail(this.denounce!.id);
        },
        error: (err) => {
          this.snackBar.open(err.message || 'Error al enviar mensaje', 'Cerrar', {
            duration: 3000,
          });
        },
      });
  }

  goBack(): void {
    this.router.navigate(['/wallet/admin/denounces']);
  }

  openAttachment(att: AttachmentRef): void {
    if (!this.denounce) return;
    this.denounceService.getAttachmentUrl(this.denounce.id, att.id).subscribe({
      next: (res) => {
        this.dialog.open(AdminAttachmentDialogComponent, {
          data: { url: res.url, fileName: res.fileName, fileType: res.fileType },
          panelClass: 'attachment-dialog-panel',
          maxWidth: '95vw',
          maxHeight: '95vh',
          autoFocus: false,
        });
      },
      error: (err) => {
        this.snackBar.open(err.message || 'No se pudo abrir el archivo', 'Cerrar', {
          duration: 3000,
        });
      },
    });
  }
}
